import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { IUser, getUser } from '../store/user';
import { SSEHubReportHistoryService } from 'app/shared/SSEHubClient/report-history.service';
import { ErrorHandlerService } from '@shared/error-handler/error-handler.service';
import { of, firstValueFrom } from 'rxjs';
import { filter, take, map, switchMap } from 'rxjs/operators';
import { PrivilegesService } from '@shared/privileges/privileges.service';

// Functional guard for report detail authorization
export const reportDetailAuthGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean> => {
  const store = inject(Store<State>);
  const sseHubReportHistoryService = inject(SSEHubReportHistoryService);
  const errorHandlerService = inject(ErrorHandlerService);
  const privilegesService = inject(PrivilegesService);

  return firstValueFrom(
    store.select(getUser).pipe(
      filter(user => user),
      take(1),
      switchMap(user => {
        if (user == null || route == null) {
          return of(false);
        }

        const reportId = route.params.reportId;

        if (privilegesService.isSuperAdminUser(user)) {
          return of(true);
        }

        return sseHubReportHistoryService.getReportAuthorization(reportId, user.id).pipe(
          map((response) => {
            const responseMap = new Map(Object.entries(response));
            if (responseMap.get('reportAccess') === false) {
              errorHandlerService.UnAuthorized();
            }
            return responseMap.get('reportAccess') || false;
          })
        );
      })
    )
  );
};

// Functional guard for report search authorization
export const reportSearchAuthGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot
): Promise<boolean> => {
  const store = inject(Store<State>);
  const errorHandlerService = inject(ErrorHandlerService);

  return firstValueFrom(
    store.select(getUser).pipe(
      filter(user => user),
      take(1),
      switchMap(user => {
        if (user == null || route == null) {
          return of(false);
        }

        if (user.profileId === 7) {
          return of(true);
        } else {
          errorHandlerService.UnAuthorized();
          return of(false);
        }
      })
    )
  );
};
